import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { SettingsCard, SettingsCards, SettingsFooter, SettingsPageHeader } from '../SettingsShell/SettingsPage';
import { SetSelect } from '../SettingsShell/controls';
import type { BrainRule, KbCategory, TrainKind, TrainRow } from '../../settings-brain/types';

export interface TrainElvaViewProps {
  rows: TrainRow[];
  rules: BrainRule[];
  location: string;
  /** `Write an entry` → KBAddEntry with the patient's question pre-filled. */
  onWriteEntry?: (question: string) => void;
  /** `Fix the entry` → the knowledge-base entry editor for the entry ELVA used. */
  onFixEntry?: (entryTitle: string) => void;
  /** `Change it` → the booking reasons page. */
  onChangeBooking?: (patientSaid: string) => void;
  /** `Move to Knowledge base` → the new-entry form, category + sentence filled. */
  onMoveRuleToKb?: (sentence: string, category: KbCategory) => void;
}

const CHANNEL_LABEL: Record<string, string> = { call: 'call', sms: 'text', email: 'email' };
const APPLIES = ['Every agent', 'AI Receptionist', 'Appointment Reminders', 'Recall', 'Gap Filler', 'Treatment Coordinator', 'Invisalign Coordinator', 'Accounts receivable'];

/**
 * AI Brain › Train ELVA — Your rules first (amendment 32 §3, board BR2Train).
 * Card 01 lets a practice write rules in plain words; cards 02–04 are the three
 * existing queues, each showing its two longest-waiting rows plus All n.
 */
export function TrainElvaView({ rows: initial, rules: initialRules, location, onWriteEntry, onFixEntry, onChangeBooking, onMoveRuleToKb }: TrainElvaViewProps) {
  const [rows, setRows] = useState(initial);
  const [rules, setRules] = useState(initialRules);
  const resolve = (id: string) => { setRows((p) => p.filter((r) => r.id !== id)); };
  const ofKind = (k: TrainKind) => rows.filter((r) => r.kind === k).sort((a, b) => b.waitedDays - a.waitedDays);
  const questions = ofKind('unanswered_question');
  const answers = ofKind('answer_to_check');
  const bookings = ofKind('close_call_booking');

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[900px] flex-1 overflow-y-auto px-[30px] pb-16 pt-6">
      <SettingsPageHeader
        eyebrow="Settings · AI Brain"
        titleLead="Train"
        titleAccent="ELVA"
        sub="Teach ELVA the way you’d teach a new team member: tell it your rules, and check what it wasn’t sure about."
      />

      <SettingsCards>
        <RulesCard rules={rules} setRules={setRules} onMoveRuleToKb={onMoveRuleToKb} />

        <QueueCard n="02" title="Answers to check" sub={`${String(answers.length)} waiting — ELVA answered, and the answer is worth a look.`}>
          {answers.slice(0, 2).map((r) => (
            <TrainRowLine key={r.id} title={r.patientSaid ?? ''} meta={`${r.patientName ?? ''} · ${CHANNEL_LABEL[r.channel ?? 'sms'] ?? ''} · answered from ${r.answeredFromEntry ?? ''}${r.raisedBecause === 'weak_match' ? ' · weak match' : r.raisedBecause === 'clinical' ? ' · a clinical question' : ''}`}
              actions={<><GhostBtn onClick={() => { resolve(r.id); }}>That’s right</GhostBtn><GhostBtn onClick={() => { onFixEntry?.(r.answeredFromEntry ?? ''); }}>Fix the entry</GhostBtn></>} />
          ))}
          <AllRow n={answers.length} />
        </QueueCard>

        <QueueCard n="03" title="Questions with no answer" sub={`${String(questions.length)} waiting — patients asked, and no entry covers it.`}>
          {questions.slice(0, 2).map((r) => (
            <TrainRowLine key={r.id} title={r.question ?? ''} meta={`asked ${String(r.askedCount ?? 0)} times · last ${r.lastAsked ?? ''}`}
              actions={<><GhostBtn onClick={() => { onWriteEntry?.(r.question ?? ''); }}>Write an entry</GhostBtn><GhostBtn onClick={() => { resolve(r.id); }}>Ignore</GhostBtn></>} />
          ))}
          <AllRow n={questions.length} />
        </QueueCard>

        <QueueCard n="04" title="Bookings that were a close call" sub={`${String(bookings.length)} waiting — ELVA picked a reason and wasn’t sure.`}>
          {bookings.slice(0, 2).map((r) => (
            <TrainRowLine key={r.id} title={r.patientSaid ?? ''} meta={`Booked ${r.bookedReason ?? ''}${r.runnerUpReason !== undefined ? ` · ${r.runnerUpReason} was nearly as good` : ''}`}
              actions={<><GhostBtn onClick={() => { resolve(r.id); }}>Right</GhostBtn><GhostBtn onClick={() => { onChangeBooking?.(r.patientSaid ?? ''); }}>Change it</GhostBtn><GhostBtn onClick={() => { resolve(r.id); }}>Not a booking</GhostBtn></>} />
          ))}
          <AllRow n={bookings.length} />
        </QueueCard>
      </SettingsCards>

      <SettingsFooter location={location} />
    </div>
  );
}

function RulesCard({ rules, setRules, onMoveRuleToKb }: { rules: BrainRule[]; setRules: React.Dispatch<React.SetStateAction<BrainRule[]>>; onMoveRuleToKb?: ((s: string, c: KbCategory) => void) | undefined }) {
  const [text, setText] = useState('');
  const [appliesTo, setAppliesTo] = useState('Every agent');
  const [reading, setReading] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const needsChange = rules.filter((r) => r.status === 'needs_change').length;

  const add = () => {
    const t = text.trim();
    if (t === '') return;
    const id = `r${String(Date.now())}`;
    setReading(id);
    setRules((p) => [...p, { id, text: t, readsAs: '', appliesTo, addedBy: 'You', addedAt: 'just now', status: 'active' }]);
    setText('');
    window.setTimeout(() => {
      // Restate the rule the person wrote, rather than a generic line (amendment 34 §6).
      const restated = `${t.replace(/\.$/, '')}, on ${appliesTo === 'Every agent' ? 'every agent' : appliesTo}.`;
      setRules((p) => p.map((r) => (r.id === id ? { ...r, readsAs: restated } : r)));
      setReading(null);
    }, 1000);
  };

  return (
    <SettingsCard n="01" id="your-rules" title="Your rules" sub="Tell ELVA how your practice works, in your own words. Every agent follows them." headRight={<span className="text-[11.5px] text-[var(--text-muted)]">{rules.length} rules · {needsChange} needs a change</span>}>
      {/* add box */}
      <div className="border-b border-[var(--border-default)] px-6 py-4">
        <textarea value={text} onChange={(e) => { setText(e.target.value); }} placeholder="Write a rule the way you’d tell a new receptionist…" className="h-[64px] w-full resize-none rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]" />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-[var(--text-muted)]">Applies to</span>
          <SetSelect value={appliesTo} options={APPLIES.map((a) => ({ value: a, label: a }))} onChange={setAppliesTo} ariaLabel="Applies to" />
          <span className="text-[11.5px] text-[var(--text-muted)]">ELVA shows how it reads the rule before it’s used</span>
          <Button variant="primary" size="sm" className="ml-auto" onClick={add}>Add rule</Button>
        </div>
      </div>

      {rules.map((r, i) => {
        const isReading = reading === r.id;
        const editing = editingId === r.id;
        return (
          <div key={r.id} className={cn('grid grid-cols-[1fr_auto] items-start gap-3 px-6 py-3.5', i < rules.length - 1 ? 'border-b border-[var(--border-default)]' : '')}>
            <div className="min-w-0">
              {editing ? (
                <div>
                  <textarea value={editText} onChange={(e) => { setEditText(e.target.value); }} className="h-[56px] w-full resize-none rounded-[8px] border border-[var(--brand-indigo)] px-3 py-2 text-[13px] outline-none" />
                  <div className="mt-1.5 flex gap-2">
                    <Button variant="primary" size="sm" onClick={() => { setRules((p) => p.map((x) => (x.id === r.id ? { ...x, text: editText } : x))); setEditingId(null); }}>Save</Button>
                    <Button variant="secondary" size="sm" onClick={() => { setEditingId(null); }}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-[13px] font-semibold text-[var(--text-primary)]">“{r.text}”</div>
                  {isReading ? (
                    <div className="mt-1 flex items-center gap-2 text-[12px] text-[var(--text-muted)]"><Spinner /> Reading your rule…</div>
                  ) : r.status === 'needs_change' && r.fix ? (
                    <div className="mt-1.5 rounded-[8px] border border-[color-mix(in_srgb,var(--warn-amber)_30%,transparent)] bg-[var(--warn-amber-fill)] px-3 py-2 text-[12px] text-[var(--text-secondary)]">
                      <span className="font-semibold text-[var(--warn-amber)]">ELVA reads it as</span> {r.readsAs} {r.fix.note} <span className="font-semibold">Put “{r.fix.sentence}” in the Knowledge base.</span>
                      <button type="button" className="ml-1 font-semibold text-[var(--brand-indigo)] hover:underline" onClick={() => { onMoveRuleToKb?.(r.fix?.sentence ?? '', r.fix?.category ?? 'policies'); }}>Move to Knowledge base →</button>
                    </div>
                  ) : (
                    <div className="mt-1 text-[12px] text-[var(--text-secondary)]"><span className="text-[10.5px] font-bold uppercase tracking-[0.05em] text-[var(--text-muted)]">ELVA reads it as </span>{r.readsAs}</div>
                  )}
                  <div className="mt-1 text-[11px] text-[var(--text-muted)]">{r.appliesTo} · added by {r.addedBy}, {r.addedAt}{r.usedCount !== undefined ? ` · used ${String(r.usedCount)} times` : ''}</div>
                </>
              )}
            </div>
            {!editing ? (
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <span className={cn('rounded-full px-2 py-0.5 text-[10.5px] font-semibold', r.status === 'active' ? 'bg-[var(--money-green-fill)] text-[#1b5f45]' : 'bg-[var(--warn-amber-fill)] text-[var(--warn-amber)]')}>{r.status === 'active' ? 'Active' : 'Needs a change'}</span>
                <span className="flex gap-2 text-[11.5px]">
                  <button type="button" className="font-semibold text-[var(--brand-indigo)] hover:underline" onClick={() => { setEditingId(r.id); setEditText(r.text); }}>Edit</button>
                  <button type="button" className="font-semibold text-[var(--alert-red)] hover:underline" onClick={() => { setConfirmId(r.id); }}>Remove</button>
                </span>
              </div>
            ) : null}
            {confirmId === r.id ? (
              <div className="col-span-2 mt-1 flex items-center justify-end gap-2 rounded-[8px] bg-[var(--surface-sunken)] px-3 py-2 text-[12px]">
                <span className="mr-auto text-[var(--text-secondary)]">Remove this rule?</span>
                <Button variant="secondary" size="sm" onClick={() => { setConfirmId(null); }}>Cancel</Button>
                <Button variant="danger" size="sm" onClick={() => { setRules((p) => p.filter((x) => x.id !== r.id)); setConfirmId(null); }}>Remove</Button>
              </div>
            ) : null}
          </div>
        );
      })}
    </SettingsCard>
  );
}

function QueueCard({ n, title, sub, children }: { n: string; title: string; sub: string; children: React.ReactNode }) {
  return <SettingsCard n={n} title={title} sub={sub}>{children}</SettingsCard>;
}
function TrainRowLine({ title, meta, actions }: { title: string; meta: string; actions: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-[var(--border-default)] px-6 py-3 last:border-0">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-[var(--text-primary)]">“{title}”</div>
        <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">{meta}</div>
      </div>
      <div className="flex gap-1.5">{actions}</div>
    </div>
  );
}
function AllRow({ n }: { n: number }) {
  return <div className="px-6 py-2.5"><button type="button" className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline">All {n}</button></div>;
}
function GhostBtn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return <button type="button" onClick={onClick} className="rounded-[8px] border border-[var(--border-default)] px-2.5 py-1 text-[12px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]">{children}</button>;
}
function Spinner() {
  return <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[var(--border-default)] border-t-[var(--brand-indigo)]" />;
}
